import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { fetchGameSummary, recalculateGameWPA } from '../../api/games';
import { useAtom } from 'jotai';
import { currentUserAtom } from '../../auth/AuthAtoms';
import { translateOuts, resultTypes, findSub, isResultTypePlateAppearance } from '../../utils/gameUtils';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRobot } from '@fortawesome/free-solid-svg-icons';
import { fetchUserCampaignContestByID } from '../../api/campaigns';
import GLOBALS from '../../config/globals';
import LoadingSpinner from '../../components/LoadingSpinner';
import RewardItems from '../../components/RewardItems';
import SpecialGameReward from '../../components/SpecialGameReward';
import SpecialGameOverlay from '../../components/SpecialGameOverlay';

const sortLineup = (lineup, events) => {
    // Build map of pitcher lineupID → first event index (chronological order)
    const pitcherOrder = new Map();
    if (events) {
        const sorted = [...events].sort((a, b) => Number(a.gameEventID) - Number(b.gameEventID));
        sorted.forEach((event, idx) => {
            const pid = event.pitcherLineup?.gameLineupID;
            if (pid != null && !pitcherOrder.has(pid)) {
                pitcherOrder.set(pid, idx);
            }
        });
    }

    return [...lineup].sort((a, b) => {
        const aIsPitcher = a.lineupOrder === null;
        const bIsPitcher = b.lineupOrder === null;
        // Batters first, sorted by lineup order
        if (!aIsPitcher && !bIsPitcher) return a.lineupOrder - b.lineupOrder;
        if (!aIsPitcher && bIsPitcher) return -1;
        if (aIsPitcher && !bIsPitcher) return 1;
        // Both pitchers — sort by first appearance
        const aOrder = pitcherOrder.get(a.gameLineupID) ?? Infinity;
        const bOrder = pitcherOrder.get(b.gameLineupID) ?? Infinity;
        return aOrder - bOrder;
    });
};

const generateBoxScore = game => {
    const playerStats = {};

    game.GameEvents.forEach(event => {
        const { batterLineup, pitcherLineup, resultType } = event;
        const isPlateAppearance = isResultTypePlateAppearance(resultType);
        //TODO: I think we can do this by lineupID?
        if (batterLineup) {
            const batterID = batterLineup.gameLineupID;

            if (!playerStats[batterID]) {
                playerStats[batterID] = {
                    plateAppearances: 0,
                    ab: 0,
                    h1: 0,
                    h2: 0,
                    h3: 0,
                    rbi: 0,
                    bb: 0,
                    so: 0,
                    hr: 0,
                    r: 0,
                    sb: 0,
                    cs: 0,
                    sh: 0,
                    gidp: 0,
                    wpa: 0,
                };
            }
            if (isPlateAppearance) {
                playerStats[batterID].ab += 1;
                playerStats[batterID].plateAppearances += 1;
                if (resultType === resultTypes.H1) playerStats[batterID].h1 += 1;
                if (resultType === resultTypes.H2) playerStats[batterID].h2 += 1;
                if (resultType === resultTypes.H3) playerStats[batterID].h3 += 1;
                if (resultType === resultTypes.HR) playerStats[batterID].hr += 1;
                if (resultType === resultTypes.BB) {
                    playerStats[batterID].bb += 1;
                    playerStats[batterID].ab -= 1;
                }
                if (event.sh) {
                    playerStats[batterID].sh += event.sh;
                    playerStats[batterID].ab -= 1;
                }
                if (resultType === resultTypes.SO) playerStats[batterID].so += 1;
                if (resultType === resultTypes.OO) playerStats[batterID].oo += 1;

                if (event.rbi && !event.gidp) playerStats[batterID].rbi += event.rbi;
                if (event.gidp) playerStats[batterID].gidp += event.gidp;
                if (event.wpa != null) playerStats[batterID].wpa += event.wpa;
            }
            //TODO runs and steals don't need a PA
            if (event.runs) playerStats[batterID].r += event.runs;
            if (event.sb) playerStats[batterID].sb += event.sb;
            if (event.cs) playerStats[batterID].cs += event.cs;
        }

        if (pitcherLineup) {
            const pitcherID = pitcherLineup.gameLineupID;

            if (!playerStats[pitcherID]) {
                playerStats[pitcherID] = {
                    plateAppearances: 0,
                    ipOuts: 0,
                    ab: 0,
                    h1: 0,
                    h2: 0,
                    h3: 0,
                    rbi: 0,
                    bb: 0,
                    so: 0,
                    hr: 0,
                    r: 0,
                    sb: 0,
                    cs: 0,
                    sh: 0,
                    gidp: 0,
                    wpa: 0,
                };
            }
            if (isPlateAppearance) {
                playerStats[pitcherID].plateAppearances += 1;
                if (resultType === resultTypes.H1) playerStats[pitcherID].h1 += 1;
                if (resultType === resultTypes.H2) playerStats[pitcherID].h2 += 1;
                if (resultType === resultTypes.H3) playerStats[pitcherID].h3 += 1;
                if (resultType === resultTypes.HR) playerStats[pitcherID].hr += 1;
                if (resultType === resultTypes.BB) playerStats[pitcherID].bb += 1;
                if (resultType === resultTypes.SO) {
                    playerStats[pitcherID].so += 1;
                    playerStats[pitcherID].ipOuts += 1;
                }
                if (resultType === resultTypes.OO) {
                    playerStats[pitcherID].oo += 1;
                    playerStats[pitcherID].ipOuts += event.outsOnPlay ?? 1;
                }
                if (event.rbi) playerStats[pitcherID].rbi += event.rbi;
                if (event.wpa != null) playerStats[pitcherID].wpa -= event.wpa;
            }

            //TODO runs and steals don't need a PA
            if (event.runs) playerStats[pitcherID].r += event.runs;
            if (event.sb) playerStats[pitcherID].sb += event.sb;
            if (event.cs) playerStats[pitcherID].cs += event.cs;
        }
    });

    return playerStats;
};

const GameSummaryPage = () => {
    const { gameID } = useParams();
    const [game, setGame] = useState(null);
    const [userCampaignContest, setUserCampaignContest] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [boxScoreExpanded, setBoxScoreExpanded] = useState(true);
    const [eventsExpanded, setEventsExpanded] = useState(false);
    const [currentUser] = useAtom(currentUserAtom);
    const [recalculating, setRecalculating] = useState(false);

    const handleRecalculateWPA = async () => {
        setRecalculating(true);
        try {
            const result = await recalculateGameWPA(gameID);
            toast.success(`Recalculated WPA for ${result.updatedCount} events`);
            const updatedGame = await fetchGameSummary(gameID);
            setGame(updatedGame);
        } catch (err) {
            toast.error('Failed to recalculate WPA: ' + (err.response?.data?.error || err.message));
        } finally {
            setRecalculating(false);
        }
    };

    //userCampaignContest && userCampaignContest.UserCampaignContests.length && !userCampaignContest.result
    useEffect(() => {
        const getGameStatus = async () => {
            setLoading(true);
            setError(null);
            try {
                const gameData = await fetchGameSummary(gameID);
                setGame(gameData);
                if (gameData && gameData.userCampaignContestID) {
                    const userCampaignContestData = await fetchUserCampaignContestByID(gameData.userCampaignContestID);
                    setUserCampaignContest(userCampaignContestData);
                }
            } catch (error) {
                setError('Error fetching game status: ' + error.message);
                toast.error('Error fetching game status: ' + error.message);
            } finally {
                setLoading(false);
            }
        };

        getGameStatus();
    }, [gameID]);

    const boxScore = useMemo(() => (game ? generateBoxScore(game) : {}), [game]);

    if (loading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <p className="error">{error}</p>;
    }

    const homeParticipant = game.GameParticipants?.find(participant => participant.role === 'home');
    const awayParticipant = game.GameParticipants?.find(participant => participant.role === 'away');
    const latestEvent = game.GameEvents?.[0];

    const toggleBoxScore = () => setBoxScoreExpanded(prev => !prev);
    const toggleEvents = () => setEventsExpanded(prev => !prev);

    const normalizedSpecialType = game?.specialGameType?.toLowerCase() || '';
    const specialGameClass = GLOBALS.SPECIAL_GAME_BG[normalizedSpecialType] ?? '';

    return (
        <div className={`game-summary-page ${specialGameClass}`.trim()}>
            <SpecialGameOverlay specialGameType={game?.specialGameType} />
            <h2>Game Summary</h2>
            {game && (
                <div>
                    <h4>
                        {homeParticipant && (
                            <span>
                                Home: {homeParticipant.UserTeam?.name} - {latestEvent?.homeScore} &nbsp;&nbsp;&nbsp;
                            </span>
                        )}
                        {awayParticipant && (
                            <span>
                                Visitor: {awayParticipant.UserTeam?.name} - {latestEvent?.visitingScore}
                            </span>
                        )}
                    </h4>
                    {userCampaignContest && !userCampaignContest.result && (
                        <Link to={`/campaign-contests/${userCampaignContest.userCampaignContestID}/play`} className="button">
                            Continue Campaign
                        </Link>
                    )}
                    {game.rewardsAwarded && (
                        <div className="game-summary-rewards">
                            <h4>Rewards Earned</h4>
                            <div className="game-summary-rewards-row">
                                <RewardItems reward={game.rewardsAwarded.game} hidePerkDetails />
                                {game.rewardsAwarded.playoff?.reward && game.rewardsAwarded.didUserWin && (
                                    game.rewardsAwarded.playoff.reward.map((pack, i) => (
                                        <div key={`playoff-${i}`} className="postgame-reward-item">
                                            <div className="postgame-reward-playoff-icon">
                                                🏆
                                                {pack.amount > 1 && <span className="postgame-reward-multiplier">x{pack.amount}</span>}
                                            </div>
                                            <div className="postgame-reward-label">
                                                {pack.seriesKey.replace('tournament-', '').replace(/^\w/, c => c.toUpperCase())} Pack
                                            </div>
                                        </div>
                                    ))
                                )}
                                {game.rewardsAwarded.game?.perkDetails?.length > 0 && (
                                    <div className="game-summary-perks">
                                        {game.rewardsAwarded.game.perkDetails.map((p, i) => (
                                            <span key={i} className={`game-summary-perk ${p.result.includes('+') ? '' : 'game-summary-perk-miss'}`}>
                                                {p.result}
                                            </span>
                                        ))}
                                    </div>
                                )}
                                <SpecialGameReward specialGame={game.rewardsAwarded.specialGame} />
                            </div>
                            {game.rewardsAwarded.playoff && (
                                <div className="game-summary-playoff-banner">
                                    Postseason Push: <strong>{game.rewardsAwarded.playoff.roundName}</strong> — {game.rewardsAwarded.didUserWin ? 'Victory' : 'Eliminated'}
                                </div>
                            )}
                            {game.rewardsAwarded.campaign?.result === GLOBALS.RESULT.WIN && game.rewardsAwarded.campaign.reward && (
                                <div className="postgame-rewards-campaign campaign-win game-summary-campaign-section">
                                    <div className="postgame-rewards-campaign-header">Campaign Complete!</div>
                                    <div className="postgame-rewards-campaign-record">
                                        {game.rewardsAwarded.campaign.contestName} — Won {game.rewardsAwarded.campaign.wins}-{game.rewardsAwarded.campaign.losses}
                                    </div>
                                    <RewardItems reward={game.rewardsAwarded.campaign.reward} hidePerkDetails />
                                </div>
                            )}
                        </div>
                    )}
                    {currentUser?.isAdmin && game?.isFinal && (
                        <button
                            onClick={handleRecalculateWPA}
                            disabled={recalculating}
                        >
                            {recalculating ? 'Recalculating...' : 'Recalculate WPA'}
                        </button>
                    )}
                    <button onClick={toggleBoxScore}>{boxScoreExpanded ? 'Hide Box Score' : 'Show Box Score'}</button>
                    {boxScoreExpanded && (
                        <div className="box-score">
                            <h3>Box Score</h3>
                            {game.GameParticipants?.map(participant => (
                                <div key={participant.gameParticipantID} className="participant">
                                    <h4>
                                        {participant.role} Team: {participant.UserTeam?.name}{' '}
                                        {participant.userID ? '' : <FontAwesomeIcon icon={faRobot} />}
                                    </h4>
                                    <div className="table-scroll-wrapper">
                                    <table className="table-striped box-score-table stats-table">
                                        <thead>
                                            <tr>
                                                <th>#</th>
                                                <th>Card</th>
                                                <th>POS</th>
                                                <th>AB<br/>IP</th>
                                                <th className="stat-th">H1</th>
                                                <th className="stat-th">H2</th>
                                                <th className="stat-th">H3</th>
                                                <th className="stat-th">HR</th>
                                                <th className="stat-th">R</th>
                                                <th className="stat-th">RBI</th>
                                                <th className="stat-th">BB</th>
                                                <th className="stat-th">SO</th>
                                                <th className="stat-th">SB</th>
                                                <th className="stat-th">CS</th>
                                                <th className="stat-th">WPA</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {sortLineup(participant.GameLineups, game.GameEvents)?.map(lineup => {
                                                const lineupID = lineup.gameLineupID;
                                                //TODO: it's not that simple, if a player started the game, but was instantly removed?
                                                if (!boxScore[lineupID]?.plateAppearances) {
                                                    return null;
                                                }

                                                return (
                                                    <tr key={lineupID}>
                                                        <td>
                                                            {lineup.lineupOrder !== null ? lineup.lineupOrder : 'P'}
                                                        </td>
                                                        <td className="card-name-cell">
                                                            <span className="card-year">{lineup.Card?.year} </span>
                                                            {lineup.Card?.name}
                                                            <span className="card-rating"> ({lineup.Card?.rating})</span>
                                                        </td>
                                                        <td>{lineup.fieldingPosition}</td>
                                                        <td>
                                                            {boxScore[lineupID]?.ab ||
                                                                (boxScore[lineupID]?.ipOuts / 3).toFixed(1) ||
                                                                0}
                                                        </td>
                                                        <td>{boxScore[lineupID]?.h1 || 0}</td>
                                                        <td>{boxScore[lineupID]?.h2 || 0}</td>
                                                        <td>{boxScore[lineupID]?.h3 || 0}</td>
                                                        <td>{boxScore[lineupID]?.hr || 0}</td>
                                                        <td>{boxScore[lineupID]?.r || 0}</td>
                                                        <td>{boxScore[lineupID]?.rbi || 0}</td>
                                                        <td>{boxScore[lineupID]?.bb || 0}</td>
                                                        <td>{boxScore[lineupID]?.so || 0}</td>
                                                        <td>{boxScore[lineupID]?.sb || 0}</td>
                                                        <td>{boxScore[lineupID]?.cs || 0}</td>
                                                        <td className={
                                                            boxScore[lineupID]?.wpa >= 0 ? 'wpa-positive' : 'wpa-negative'
                                                        }>
                                                            {boxScore[lineupID]?.wpa != null
                                                                ? (boxScore[lineupID].wpa >= 0 ? '+' : '') + boxScore[lineupID].wpa.toFixed(3)
                                                                : '—'}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                    <button onClick={toggleEvents}>{eventsExpanded ? 'Hide Game Events' : 'Show Game Events'}</button>
                    {/* add in when the players were subed */}
                    {eventsExpanded && (
                        <div className="events-container">
                            <h3>All Game Events:</h3>
                            <div className="table-scroll-wrapper">
                            <table className="box-score-table events-table">
                                <thead>
                                    <tr>
                                        <th>Score</th>
                                        <th>Outs</th>
                                        <th>On</th>
                                        <th>Result</th>
                                        <th className="events-desc">Description</th>
                                        <th>WPA</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {[...game.GameEvents].sort((a, b) => Number(a.gameEventID) - Number(b.gameEventID)).map((event, idx) => {
                                        const sub = findSub(event.gameEventID, game);
                                        const wpa = event.wpa != null ? event.wpa : null;
                                        const wpaDisplay = wpa != null ? `${wpa >= 0 ? '+' : ''}${(wpa * 100).toFixed(1)}%` : '—';
                                        const wpaClass = wpa == null ? '' : wpa > 0 ? 'wpa-positive' : wpa < 0 ? 'wpa-negative' : '';
                                        const rowClass = idx % 2 === 0 ? 'event-even' : 'event-odd';
                                        const runners = [
                                            event.firstBaseRunnerLineupID ? 'R1' : null,
                                            event.secondBaseRunnerLineupID ? 'R2' : null,
                                            event.thirdBaseRunnerLineupID ? 'R3' : null,
                                        ].filter(Boolean).join('-');
                                        return (
                                            <React.Fragment key={event.gameEventID}>
                                                <tr className={rowClass}>
                                                    <td>
                                                        H:{event.homeScore} - V:{event.visitingScore}
                                                    </td>
                                                    <td>
                                                        <span className="outs-full">{translateOuts(event.ipOuts).complete}</span>
                                                        <span className="outs-short">{`${translateOuts(event.ipOuts).inningHalf[0]}${translateOuts(event.ipOuts).inning} ${translateOuts(event.ipOuts).outs}o`}</span>
                                                    </td>
                                                    <td className="runners-cell">{runners || '—'}</td>
                                                    <td>{event.resultType}</td>
                                                    <td className="events-desc">{event.playByPlay?.length ? event.playByPlay.join(' · ') : event.resultDescription}</td>
                                                    <td className={`wpa-cell ${wpaClass}`}>{wpaDisplay}</td>
                                                </tr>
                                                <tr className={`events-desc-row ${rowClass}`}>
                                                    <td colSpan="5" className="events-desc-mobile">{event.playByPlay?.length ? event.playByPlay.join(' · ') : event.resultDescription}</td>
                                                </tr>
                                                {sub && (
                                                    <tr className={rowClass}>
                                                        <td colSpan="5">{sub}</td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default GameSummaryPage;
